<template>
  <section class="footer-section" ref="footerSection">
    <v-card tile class="footer-card">
      <v-card-text class="footer-card__inner">
        <div class="container-xl">
          <!-- Main Footer Content -->
          <v-row class="footer-top">
            <!-- Company Information -->
            <v-col cols="12" md="4" class="mb-6 mb-md-0">
              <div class="footer-brand">
                <h3 class="footer-brand__title">PosSmart</h3>
                <p class="footer-brand__desc">
                  {{ $t("footer_description") || footerDescription }}
                </p>
              </div>
            </v-col>

            <!-- Quick Links -->
            <v-col cols="12" sm="6" md="2" class="mb-6 mb-md-0">
              <h4 class="footer-heading">
                {{ $t("quick_links") || "QUICK LINKS" }}
              </h4>
              <v-list class="footer-list">
                <v-list-item
                  v-for="(link, index) in quickLinks"
                  :key="index"
                  :to="link.to"
                  class="footer-list__item"
                >
                  <v-list-item-title class="footer-link footer-link--list">
                    {{ $t(link.text) || link.text }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-col>

            <!-- Help & Support -->
            <v-col cols="12" sm="6" md="2" class="mb-6 mb-md-0">
              <h4 class="footer-heading">
                {{ $t("help_support") || "ການຊ່ວຍເຫຼືອ" }}
              </h4>
              <v-list class="footer-list">
                <v-list-item
                  v-for="(link, index) in helpLinks"
                  :key="index"
                  :to="link.to"
                  class="footer-list__item"
                >
                  <v-list-item-title class="footer-link footer-link--list">
                    {{ $t(link.text) || link.text }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-col>

            <!-- Newsletter -->
            <v-col cols="12" md="4">
              <h4 class="footer-heading footer-heading--tight">
                {{ $t("newsletter") || "ຈົດໝາຍຂ່າວ" }}
              </h4>
              <p class="footer-body mb-4">
                {{ $t("newsletter_description") || "ຮັບຂ່າວສານ ແລະ ດີລລ່າສຸດ" }}
              </p>
              <div class="newsletter-wrapper">
                <v-form
                  @submit.prevent="subscribeNewsletter"
                  class="newsletter-form"
                >
                  <v-text-field
                    v-model="email"
                    :placeholder="$t('email_placeholder') || 'you@example.com'"
                    outlined
                    dense
                    hide-details
                    class="newsletter-input"
                    type="email"
                    autocomplete="email"
                    aria-label="Email"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small class="newsletter-input__icon">
                        fal fa-envelope
                      </v-icon>
                    </template>
                    <template v-slot:append>
                      <v-btn
                        type="submit"
                        small
                        depressed
                        class="newsletter-btn"
                        :loading="subscribing"
                      >
                        {{ $t("subscribe") || "ຕິດຕາມ" }}
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-form>
              </div>
              <!-- Social Media Icons -->
              <div class="footer-social">
                <a
                  v-for="(item, index) in socialLinks"
                  :key="index"
                  :href="item.to"
                  :target="item.target || '_blank'"
                  :rel="item.rel || 'noopener noreferrer'"
                  class="social-link"
                  :aria-label="item.name"
                >
                  <img
                    :src="item.image"
                    :alt="item.name"
                    class="social-image"
                  />
                </a>
              </div>
            </v-col>
          </v-row>

          <!-- Divider -->
          <v-divider class="footer-divider" />

          <!-- Bottom Footer -->
          <v-row align="center" class="footer-bottom">
            <v-col cols="12" md="4" class="text-center text-md-left">
              <span class="footer-meta">
                &copy; {{ currentYear }} PosSmart.
                {{ $t("all_rights_reserved") || "ສະຫງວນລິຂະສິດທັງໝົດ" }}
                <template v-if="version"> · v{{ version }}</template>
              </span>
            </v-col>
            <v-col cols="12" md="4" class="text-center">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    text
                    small
                    class="footer-lang-btn"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small class="mr-1">fal fa-globe</v-icon>
                    {{ currentLanguage }}
                    <v-icon small class="ml-1">fal fa-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="(lang, index) in languages"
                    :key="index"
                    @click="changeLanguage(lang.code)"
                  >
                    <v-list-item-title>{{ lang.name }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
            <v-col cols="12" md="4" class="text-center text-md-right">
              <div
                class="d-flex justify-center justify-md-end align-center flex-wrap"
              >
                <a
                  v-for="(link, index) in legalLinks"
                  :key="index"
                  :href="link.to"
                  class="footer-link footer-link--legal mr-3"
                >
                  {{ $t(link.text) || link.text }}
                </a>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>

    <!-- Scroll to Top Button -->
    <v-fab-transition>
      <v-btn
        v-show="showScrollTop"
        color="secondary"
        dark
        fixed
        bottom
        right
        fab
        small
        class="scroll-top-btn"
        @click="scrollToTop"
      >
        <v-icon>fal fa-arrow-up</v-icon>
      </v-btn>
    </v-fab-transition>
  </section>
</template>

<script>
export default {
  name: "LandingFooter",
  data() {
    return {
      version: process.env.VUE_APP_VERSION,
      phoneNumber: "+8562028492947",
      email: "",
      subscribing: false,
      showScrollTop: false,
      footerDescription:
        "ລະບົບ PosSmart ແມ່ນລະບົບຈັດການໜ້າຮ້ານຄ້າອອນໄລນ໌ທີ່ໃຊ້ງານງ່າຍ, ພັດທະນາໂດຍຄົນລາວ 100%.",
      socialLinks: [
        {
          name: "Facebook",
          image:
            "https://devla.s3.ap-southeast-1.amazonaws.com/pos/images/facebook.png",
          to: "https://www.facebook.com/pos.devla.la",
          class: "facebook-hover",
        },
        {
          name: "TikTok",
          image:
            "https://devla.s3.ap-southeast-1.amazonaws.com/pos/images/tiktok.png",
          to: "https://www.tiktok.com/@possmart_devla",
          class: "tiktok-hover",
        },
        {
          name: "YouTube",
          image:
            "https://devla.s3.ap-southeast-1.amazonaws.com/pos/images/youtube.png",
          to: "https://youtube.com/@possmart",
          class: "youtube-hover",
        },
        {
          name: "WhatsApp",
          image:
            "https://devla.s3.ap-southeast-1.amazonaws.com/pos/images/whatsapp.png",
          to: `https://wa.me/8562028492947`,
          class: "whatsapp-hover",
        },
      ],
      quickLinks: [
        { text: "home", to: "/" },
        { text: "features", to: "/#features" },
        { text: "packages", to: "/#packages" },
        { text: "tutorials", to: "/#tutorials" },
      ],
      helpLinks: [
        { text: "faq", to: "/faq" },
        { text: "help_center", to: "/help" },
        { text: "contact_us", to: "/contact" },
      ],
      legalLinks: [
        { text: "privacy_policy", to: "/privacy" },
        { text: "terms_of_service", to: "/terms" },
      ],
      languages: [
        { code: "la", name: "ລາວ" },
        { code: "en", name: "English" },
        { code: "th", name: "ไทย" },
      ],
    };
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
    currentLanguage() {
      const lang = this.$i18n.locale || "la";
      const langObj = this.languages.find((l) => l.code === lang);
      return langObj ? langObj.name : "ລາວ";
    },
  },
  mounted() {
    this.handleScroll();
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    async subscribeNewsletter() {
      if (!this.email || !this.validateEmail(this.email)) {
        await this.$swal.fire({
          icon: "error",
          title: this.$t("error") || "Error",
          text: this.$t("invalid_email") || "Invalid email address",
          confirmButtonColor: "#5b39c9",
        });
        return;
      }
      this.subscribing = true;
      // TODO: Implement newsletter subscription API call
      setTimeout(async () => {
        this.subscribing = false;
        await this.$swal.fire({
          icon: "success",
          title: this.$t("success") || "Success",
          text: this.$t("newsletter_subscribed") || "Successfully subscribed!",
          showConfirmButton: false,
          timer: 1500,
          confirmButtonColor: "#5b39c9",
        });
        this.email = "";
      }, 1000);
    },
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    changeLanguage(lang) {
      this.$i18n.locale = lang;
      localStorage.setItem("locale", lang);
    },
    handleScroll() {
      this.showScrollTop = window.scrollY > 300;
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
  },
};
</script>

<style scoped>
@import "@/styles/landing-light.css";

.footer-section {
  position: relative;
}

.footer-card {
  background-color: var(--bg-primary) !important;
  border-top: 1px solid var(--glass-border);
}

.footer-card__inner {
  padding-top: 52px !important;
  padding-bottom: 28px !important;
}

.footer-top {
  margin-bottom: 28px;
}

.footer-brand__title {
  font-family: "Poppins", "Noto Sans Lao", "Sarabun", sans-serif;
  font-weight: 800;
  font-size: 1.5rem;
  letter-spacing: -0.02em;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 12px;
}

.footer-brand__desc {
  font-family: "Open Sans", "Noto Sans Lao", "Sarabun", sans-serif;
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 0.95rem;
  margin: 0;
  max-width: 52ch;
}

.footer-heading {
  font-family: "Poppins", "Noto Sans Lao", "Sarabun", sans-serif;
  color: var(--text-primary);
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  margin: 0 0 14px;
}

.footer-heading--tight {
  margin-bottom: 8px;
}

.footer-body {
  color: var(--text-secondary);
  line-height: 1.7;
}

.footer-list {
  padding: 0 !important;
  background: transparent !important;
}

.footer-list__item {
  padding: 2px 0 !important;
  min-height: 32px;
}

.footer-link {
  transition: color 0.2s ease, opacity 0.2s ease;
  cursor: pointer;
  color: var(--text-secondary);
  text-decoration: none;
}

.footer-link:hover {
  color: var(--color-primary) !important;
  opacity: 1;
}

.footer-link:focus-visible {
  outline: 3px solid var(--color-primary-20);
  outline-offset: 3px;
  border-radius: 10px;
}

.footer-link--legal {
  display: inline-flex;
  padding: 6px 0;
}

.footer-link--list {
  font-size: 0.95rem;
}

/* Remove active background-color from footer links */
.footer-section ::v-deep .v-list-item--active {
  background-color: transparent !important;
}

.footer-section ::v-deep .v-list-item--active::before {
  opacity: 0 !important;
}

.footer-section ::v-deep .v-list-item--link:hover::before {
  opacity: 0 !important;
}

.footer-divider {
  border-color: var(--glass-border) !important;
  margin-bottom: 14px;
}

.footer-social {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 14px;
}

.social-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--glass-border);
  border-radius: 50%;
  background: transparent;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    transform 0.2s ease;
}

.social-image {
  width: 22px;
  height: 22px;
  object-fit: contain;
  opacity: 0.9;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.social-link:hover {
  background: var(--bg-glass-hover);
  border-color: var(--glass-border-hover);
  transform: translateY(-1px);
}

.social-link:hover .social-image {
  opacity: 1;
  transform: scale(1.02);
}

.social-link:focus-visible {
  outline: 3px solid var(--color-primary-20);
  outline-offset: 3px;
}

.newsletter-form {
  max-width: 390px;
}

.newsletter-input {
  flex: 1;
}

.newsletter-input ::v-deep .v-input__slot {
  display: flex;
  align-items: center;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.86) !important;
  min-height: 58px !important;
  padding: 6px 6px 6px 14px !important;
  border-radius: 16px !important;
  border: 1px solid var(--glass-border) !important;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06) !important;
  transition: border-color 180ms ease, box-shadow 180ms ease,
    background-color 180ms ease;
}

.newsletter-input ::v-deep .v-input__control {
  min-height: 58px !important;
}

.newsletter-input ::v-deep .v-input__prepend-inner {
  align-self: center;
  display: flex;
  align-items: center;
  height: 44px;
  margin-top: 0 !important;
  padding-left: 0;
}

.newsletter-input__icon {
  color: var(--color-primary) !important;
  opacity: 0.85;
}

.newsletter-input ::v-deep input {
  color: var(--text-primary) !important;
  padding: 10px 8px !important;
  font-family: "Open Sans", "Noto Sans Lao", "Sarabun", sans-serif;
  font-size: 0.92rem;
  font-weight: 600;
}

.newsletter-input ::v-deep input::placeholder {
  color: var(--text-muted) !important;
  font-weight: 500;
}

.newsletter-input ::v-deep .v-input__append-inner {
  align-self: center;
  display: flex;
  align-items: center;
  height: 44px;
  margin-top: 0 !important;
  padding-right: 0;
}

.newsletter-input ::v-deep .v-input--is-focused .v-input__slot {
  border-color: var(--color-primary) !important;
  background: #ffffff !important;
  box-shadow: 0 0 0 4px var(--color-primary-10) !important;
}

.newsletter-btn {
  height: 44px !important;
  min-width: 110px !important;
  min-height: 44px !important;
  margin: 0 !important;
  border-radius: 12px !important;
  padding: 0 20px !important;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-hover) 100%
  ) !important;
  color: #ffffff !important;
  font-family: "Poppins", "Noto Sans Lao", "Sarabun", sans-serif;
  font-weight: 800 !important;
  letter-spacing: 0;
  text-transform: none !important;
  box-shadow: none !important;
  transition: all 220ms cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.newsletter-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(91, 57, 201, 0.22) !important;
}

.newsletter-btn:focus-visible {
  outline: 3px solid var(--color-primary-20);
  outline-offset: 2px;
}

.footer-bottom {
  min-height: 48px;
}

.footer-meta {
  color: var(--text-muted);
  font-size: 0.9rem;
}

.footer-lang-btn {
  color: var(--text-secondary) !important;
  text-transform: none;
  border-radius: 10px;
}

.footer-lang-btn:hover {
  color: var(--color-primary) !important;
  background: var(--bg-glass-hover) !important;
}

.footer-lang-btn:focus-visible {
  outline: 3px solid var(--color-primary-20);
  outline-offset: 3px;
}

.scroll-top-btn {
  z-index: 1000;
  margin-bottom: 24px;
  margin-right: 24px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
  transition: all 0.3s ease;
}

.scroll-top-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.22);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .footer-bottom {
    text-align: center !important;
  }
}

@media (max-width: 600px) {
  .newsletter-input ::v-deep .v-input__slot {
    display: flex;
    min-height: auto !important;
    padding: 4px !important;
    flex-wrap: wrap;
  }

  .newsletter-input ::v-deep input {
    min-height: 42px;
  }

  .newsletter-btn {
    min-height: 44px !important;
    width: 100%;
    margin: 4px 0 0 !important;
  }
}
</style>
